import { useCallback, useEffect, useRef, useState } from "react";
import { apiGet } from "@/lib/api";

export interface UseApiResult<T> {
  data: T;
  dataPath: string | null;
  loading: boolean;
  error: Error | null;
  reload: () => void;
}


// useApi fetches `path` from the Go backend and returns live data, falling back
// to `fallback` (the bundled mock seed) while loading or if the API is
// unreachable — so the UI always renders meaningful content.
export function useApi<T>(path: string, fallback: T): UseApiResult<T> {
  const [data, setData] = useState<T>(fallback);
  const [dataPath, setDataPath] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [nonce, setNonce] = useState(0);
  const dataPathRef = useRef<string | null>(null);

  useEffect(() => {
    dataPathRef.current = dataPath;
  }, [dataPath]);

  const reload = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    // A reload of the same resource should retain its last response while the
    // replacement is fetched. Clearing it makes polling UIs flash empty.
    if (dataPathRef.current !== path) setDataPath(null);
    setLoading(true);
    apiGet<T>(path, ctrl.signal)
      .then((d) => {
        if (d !== null && d !== undefined) {
          setData(d);
          setDataPath(path);
        }
        setError(null);
      })
      .catch((e: unknown) => {
        if (ctrl.signal.aborted) return;
        // Keep fallback data on failure; surface the error for optional UI.
        // (Connectivity reporting happens inside lib/api's trackedFetch.)
        setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    return () => ctrl.abort();
  }, [path, nonce]);

  return { data, dataPath, loading, error, reload };
}
